import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { corporateTheme, corporateLabels } from '../themes/corporate';

const ServicesContainer = styled.div`
  width: 100%;
  padding: 6rem 2rem;
  background: ${corporateTheme.backgroundGradient};
  font-family: ${corporateTheme.fontFamily};
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
`;

const Title = styled(motion.h2)`
  font-size: clamp(2.5rem, 5vw, 4rem);
  font-weight: 700;
  color: ${corporateTheme.textPrimary};
  text-align: center;
  margin-bottom: 1rem;
  font-family: ${corporateTheme.fontFamily};
`;

const Subtitle = styled(motion.p)`
  font-size: 1.2rem;
  color: ${corporateTheme.textSecondary};
  text-align: center;
  max-width: 600px;
  margin: 0 auto 4rem;
  line-height: 1.6;
`;

const EarningMethodsGrid = styled.div`
  display: grid;
  /* 4 cards: 2x2 layout for better balance */
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  margin-bottom: 4rem;
  
  @media (max-width: 1200px) {
    /* Switch to auto-fit for medium screens */
    grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  }
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 1.5rem;
    margin-bottom: 3rem;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 1rem;
    margin-bottom: 2rem;
  }
`;

const EarningCard = styled(motion.div)`
  background: ${corporateTheme.cardBackground};
  backdrop-filter: blur(15px);
  border-radius: 20px;
  padding: 3rem;
  border: 1px solid ${corporateTheme.cardBorder};
  text-align: center;
  position: relative;
  overflow: hidden;
  box-shadow: ${corporateTheme.cardShadow};
  
  &:before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: linear-gradient(45deg, transparent, rgba(30, 64, 175, 0.1), transparent);
    transform: rotate(45deg);
    transition: all 0.6s ease;
    opacity: 0;
  }
  
  &:hover:before {
    opacity: 1;
    animation: shimmer 1.5s ease-in-out infinite;
  }
  
  @keyframes shimmer {
    0% { transform: translateX(-100%) translateY(-100%) rotate(45deg); }
    100% { transform: translateX(100%) translateY(100%) rotate(45deg); }
  }
  
  @media (max-width: 768px) {
    padding: 2rem;
    border-radius: 15px;
  }
  
  @media (max-width: 480px) {
    padding: 1.5rem;
    border-radius: 12px;
  }
`;

const EarningIcon = styled.div`
  font-size: 1.2rem;
  margin-bottom: 1.5rem;
  position: relative;
  z-index: 2;
  background: ${corporateTheme.primaryGradient};
  color: ${corporateTheme.background};
  padding: 1rem;
  border-radius: 50px;
  display: inline-block;
  font-weight: 600;
  letter-spacing: 1px;
  min-width: 120px;
`;

const EarningTitle = styled.h3`
  font-size: 1.5rem;
  color: ${corporateTheme.primary};
  margin-bottom: 1rem;
  font-weight: 600;
  position: relative;
  z-index: 2;
`;

const CommissionRate = styled.div`
  font-size: 2.5rem;
  font-weight: 900;
  background: ${corporateTheme.primaryGradient};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 1rem;
  position: relative;
  z-index: 2;
  
  @media (max-width: 480px) {
    font-size: 2rem;
  }
`;

const EarningDescription = styled.p`
  color: ${corporateTheme.textSecondary};
  line-height: 1.6;
  margin-bottom: 1.5rem;
  position: relative;
  z-index: 2;
`;

const EarningFeatures = styled.ul`
  list-style: none;
  padding: 0;
  text-align: left;
  position: relative;
  z-index: 2;
`;

const EarningFeature = styled.li`
  color: ${corporateTheme.textTertiary};
  margin-bottom: 0.5rem;
  padding-left: 1.5rem;
  position: relative;
  
  &:before {
    content: "✓";
    position: absolute;
    left: 0;
    font-size: 0.9rem;
    color: ${corporateTheme.success};
    font-weight: bold;
  }
`;

const ProcessSection = styled(motion.div)`
  background: rgba(30, 64, 175, 0.05);
  border-radius: 25px;
  padding: 4rem 3rem;
  border: 1px solid rgba(30, 64, 175, 0.2);
  margin-bottom: 4rem;
  
  @media (max-width: 768px) {
    padding: 3rem 2rem;
    border-radius: 20px;
    margin-bottom: 3rem;
  }
  
  @media (max-width: 480px) {
    padding: 2rem 1.5rem;
    border-radius: 15px;
    margin-bottom: 2rem;
  }
`;

const ProcessTitle = styled.h3`
  font-size: 2.5rem;
  color: ${corporateTheme.textPrimary};
  text-align: center;
  margin-bottom: 3rem;
  font-weight: 700;
  
  @media (max-width: 768px) {
    font-size: 2rem;
    margin-bottom: 2rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.5rem;
    margin-bottom: 1.5rem;
  }
`;

const ProcessSteps = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 3rem;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 2rem;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const ProcessStep = styled(motion.div)`
  text-align: center;
  position: relative;
`;

const StepNumber = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: ${corporateTheme.primaryGradient};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  font-weight: 900;
  color: ${corporateTheme.background};
  margin: 0 auto 1.5rem;
`;

const StepTitle = styled.h4`
  font-size: 1.3rem;
  color: ${corporateTheme.primary};
  margin-bottom: 1rem;
  font-weight: 600;
`;

const StepDescription = styled.p`
  color: ${corporateTheme.textSecondary};
  line-height: 1.6;
`;

const CTASection = styled(motion.div)`
  text-align: center;
  background: ${corporateTheme.cardBackground};
  border-radius: 20px;
  padding: 3rem;
  border: 1px solid ${corporateTheme.cardBorder};
  box-shadow: ${corporateTheme.cardShadow};
  
  @media (max-width: 768px) {
    padding: 2rem;
    border-radius: 15px;
  }
  
  @media (max-width: 480px) {
    padding: 1.5rem;
    border-radius: 12px;
  }
`;

const CTATitle = styled.h3`
  font-size: 2rem;
  color: ${corporateTheme.textPrimary};
  margin-bottom: 1rem;
  font-weight: 700;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.3rem;
  }
`;

const CTADescription = styled.p`
  color: ${corporateTheme.textSecondary};
  font-size: 1.1rem;
  margin-bottom: 2rem;
  line-height: 1.6;
`;

const CTAButton = styled(motion.button)`
  background: ${corporateTheme.primaryGradient};
  color: ${corporateTheme.background};
  border: none;
  padding: 1rem 3rem;
  font-size: 1.1rem;
  font-weight: 600;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: ${corporateTheme.cardShadow};
  font-family: ${corporateTheme.fontFamily};
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 15px 40px rgba(30, 64, 175, 0.3);
    background: linear-gradient(135deg, ${corporateTheme.primaryDark} 0%, ${corporateTheme.primary} 100%);
  }
`;

const ServicesSection = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  const earningMethods = [
    {
      icon: "E-COMMERCE",
      title: "E-commerce Revenue Share",
      commission: "Up to 45%",
      description: "Earn ongoing commissions from every customer you refer to our premium e-commerce partners.",
      features: [
        "Lifetime revenue sharing",
        "Monthly performance bonuses",
        "Real-time earnings tracking",
        "Top-tier retail brands"
      ]
    },
    {
      icon: "SAAS",
      title: "SaaS Subscription CPA",
      commission: "€150-€400",
      description: "Get paid fixed amounts for each qualified software subscription customer you bring in.",
      features: [
        "Per qualified subscriber",
        "Fast approval process",
        "High-converting offers",
        "Multiple software categories"
      ]
    },
    {
      icon: "HYBRID",
      title: "Hybrid Deals",
      commission: "CPA + RevShare",
      description: "Combine the best of both models - get immediate CPA payments plus ongoing revenue share.",
      features: [
        "Upfront CPA payments",
        "Long-term revenue sharing",
        "Flexible commission structures",
        "Custom deal negotiations"
      ]
    },
    {
      icon: "PREMIUM",
      title: "Premium Service Campaigns",
      commission: "€1,000+",
      description: "Exclusive campaigns targeting high-value customers with significantly higher payouts.",
      features: [
        "Premium customer targeting",
        "Exclusive bonus offers",
        "Dedicated account management",
        "Priority payment processing"
      ]
    }
  ];

  const processSteps = [
    {
      number: "1",
      title: "Apply & Get Approved",
      description: "Submit your application and get approved within 72 hours. Our team reviews all applications personally."
    },
    {
      number: "2",
      title: "Get Your Links & Materials",
      description: "Access your affiliate dashboard with custom tracking links, banners, and marketing materials."
    },
    {
      number: "3",
      title: "Start Promoting & Earning",
      description: "Drive traffic using our high-converting campaigns and watch your commissions grow weekly."
    }
  ];

  return (
    <ServicesContainer ref={ref} id="services">
      <Container>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          <Title variants={itemVariants}>
            How You Earn with GrowTech
          </Title>
          
          <Subtitle variants={itemVariants}>
            Multiple commission models designed to maximize your earning potential
          </Subtitle>

          <EarningMethodsGrid>
            {earningMethods.map((method, index) => (
              <EarningCard
                key={index}
                variants={itemVariants}
                whileHover={{ scale: 1.02, y: -5 }}
                transition={{ duration: 0.3 }}
              >
                <EarningIcon>{method.icon}</EarningIcon>
                <EarningTitle>{method.title}</EarningTitle>
                <CommissionRate>{method.commission}</CommissionRate>
                <EarningDescription>{method.description}</EarningDescription>
                <EarningFeatures>
                  {method.features.map((feature, idx) => (
                    <EarningFeature key={idx}>{feature}</EarningFeature>
                  ))}
                </EarningFeatures>
              </EarningCard>
            ))}
          </EarningMethodsGrid>

          <ProcessSection variants={itemVariants}>
            <ProcessTitle>Start Earning in 3 Simple Steps</ProcessTitle>
            <ProcessSteps>
              {processSteps.map((step, index) => (
                <ProcessStep key={index} variants={itemVariants}>
                  <StepNumber>{step.number}</StepNumber>
                  <StepTitle>{step.title}</StepTitle>
                  <StepDescription>{step.description}</StepDescription>
                </ProcessStep>
              ))}
            </ProcessSteps>
          </ProcessSection>

          <CTASection variants={itemVariants}>
            <CTATitle>Ready to Start Your {corporateLabels.affiliate} Journey?</CTATitle>
            <CTADescription>
              Join the GrowTech affiliate program today and start earning with industry-leading 
              commission rates and dedicated support.
            </CTADescription>
            <CTAButton
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => document.querySelector('#contact').scrollIntoView({ behavior: 'smooth' })}
            >
              Apply Now - It's Free
            </CTAButton>
          </CTASection>
        </motion.div>
      </Container>
    </ServicesContainer>
  );
};

export default ServicesSection; 